/**
 * iter286 — Bandeau promotions sur la fiche OR.
 * Les promos marquées « automatique » sont posées dès l'ouverture du dossier :
 * le bandeau affiche ce qui a été appliqué et les offres encore proposables.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgePercent, Gift, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const OrPromosBanner = ({ orId, canEdit = false, onChange }) => {
  const [etat, setEtat] = useState(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/or/${orId}/promos`);
      setEtat(data);
      if ((data.posees_automatiquement || []).length) {
        toast.success(`Promo appliquée : ${data.posees_automatiquement[0].libelle}`);
        onChange?.();
      }
    } catch {
      setEtat(null);
    }
  }, [orId, onChange]);

  useEffect(() => { charger(); }, [charger]);

  const poser = async (pid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/or/${orId}/promos/${pid}`);
      toast.success(`${data.libelle} — ${data.detail}`);
      await charger();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Application impossible");
    } finally {
      setBusy(false);
    }
  };

  const retirer = async (pid) => {
    setBusy(true);
    try {
      await api.delete(`/or/${orId}/promos/${pid}`);
      toast.success("Promotion retirée");
      await charger();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retrait impossible");
    } finally {
      setBusy(false);
    }
  };

  const appliquees = etat?.appliquees || [];
  const eligibles = etat?.eligibles || [];
  if (!appliquees.length && !eligibles.length) return null;

  return (
    <div className="mb-4 rounded-xl border border-[#52a675]/40 bg-[#f0f9f4] p-3" data-testid="or-promos-banner">
      {appliquees.map((p) => (
        <div key={p.promo_id} className="flex items-start gap-2 flex-wrap" data-testid="or-promo-appliquee">
          <Gift className="w-4 h-4 text-[#2d5f3f] mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[#2d5f3f]">
              Promo appliquée : {p.libelle}
            </div>
            <div className="text-xs text-gray-600">
              {p.offert} · {p.detail}
              {p.auto || p.applique_par === "automatique"
                ? " · posée automatiquement"
                : p.applique_par ? ` · ajoutée manuellement par ${p.applique_par}` : ""}
            </div>
          </div>
          {canEdit && (
            <Button
              size="sm" variant="ghost" disabled={busy}
              onClick={() => retirer(p.promo_id)}
              className="h-7 text-xs text-gray-500"
              data-testid={`or-promo-retirer-${p.promo_id}`}
            >
              <X className="w-3 h-3 mr-1" /> Retirer
            </Button>
          )}
        </div>
      ))}

      {eligibles.map((p) => (
        <div
          key={p.id}
          className="flex items-start gap-2 flex-wrap mt-2 pt-2 border-t border-[#52a675]/20"
          data-testid="or-promo-eligible"
        >
          <BadgePercent className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900">
              {p.beneficiaire_campagne ? "Ce client a reçu la promo" : "Promotion disponible"} : {p.libelle}
            </div>
            <div className="text-xs text-gray-600">{p.offert} · valable jusqu&apos;au {p.date_fin}</div>
          </div>
          <Button
            size="sm" disabled={busy}
            onClick={() => poser(p.id)}
            className="h-7 text-xs bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid={`or-promo-appliquer-${p.id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Appliquer
          </Button>
        </div>
      ))}

      {!!appliquees.length && !eligibles.length && (
        <Badge variant="outline" className="mt-2 text-[10px] bg-white border-[#52a675]/40 text-[#2d5f3f]">
          Trace conservée sur le dossier et dans l&apos;historique client
        </Badge>
      )}
    </div>
  );
};

export default OrPromosBanner;
